import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[4px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-primary-40 text-primary-foreground",
        secondary: "bg-grey-95",
        greyOutline: "border borde-grey-80 bg-grey-95",
        outline: "border borde-grey-80 bg-grey-99",
        gradation: "bg-gradient-40 py-[48px] border-primary-50 border",
        withIcon: "bg-grey-95 py-[8px] pl-[14px] pr-[16px] gap-[8px]",
      },
      size: {
        default: "px-4 py-2",
        xs: "min-w-[106px] pl-[14px] pr-[16px] py-[8px]",
        sm: "pl-[12px] pr-[24px] py-[12px]",
        md: "w-[132px] py-[12px] px-[48px] ",
        lg: "w-[145px] py-[12px] px-[48px]",
        xl: "w-[254px] pb-[10px] pt-[8px] px-[48px]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className })) + ""}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
